import React from 'react';
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon, Block as BlockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { formatCalendarDate } from '../../utils/dateFormatUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND } from '../../theme/brandTokens';

const StockPurchasesPanel = ({
  items = [],
  totalCount = 0,
  page,
  pageSize,
  currency,
  canCreate = false,
  canVoid = false,
  onPageChange,
  onRecord,
  onOpen,
  onVoid,
}) => {
  const { t } = useTranslation();

  return (
    <Box id="stock-purchases-panel" sx={{ p: { xs: 2, sm: 3 } }}>
      {canCreate ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <PrimaryActionButton id="record-purchase" onClick={onRecord}>
            {t('stock.purchases.record')}
          </PrimaryActionButton>
        </Box>
      ) : null}
      <Box sx={{ overflowX: 'auto', border: `1px solid ${BRAND.line}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: BRAND.background }}>
              <TableCell>{t('stock.purchases.date')}</TableCell>
              <TableCell>{t('stock.purchases.supplier')}</TableCell>
              <TableCell>{t('stock.purchases.invoice')}</TableCell>
              <TableCell align="right">{t('stock.purchases.total')}</TableCell>
              <TableCell>{t('stock.purchases.status')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">{t('stock.purchases.empty')}</Typography>
                </TableCell>
              </TableRow>
            ) : items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatCalendarDate(item.purchaseDate)}</TableCell>
                <TableCell>{item.supplierName || t('stock.purchases.noSupplier')}</TableCell>
                <TableCell>{item.invoiceReference}</TableCell>
                <TableCell align="right">{formatCurrency(item.totalAmount, currency)}</TableCell>
                <TableCell>{t(`stock.purchaseStatus.${item.status}`)}</TableCell>
                <TableCell align="right">
                  <Tooltip title={t('stock.purchases.view')}>
                    <IconButton aria-label={t('stock.purchases.view')} onClick={() => onOpen(item)} sx={{ color: BRAND.accentText }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canVoid && item.status === 'Recorded' ? (
                    <Tooltip title={t('stock.purchases.void')}>
                      <IconButton aria-label={t('stock.purchases.void')} onClick={() => onVoid(item)} sx={{ color: 'error.main' }}>
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={Math.max(0, page - 1)}
          onPageChange={(_, nextPage) => onPageChange(nextPage + 1)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
          onRowsPerPageChange={() => {}}
        />
      </Box>
    </Box>
  );
};

export default StockPurchasesPanel;
